import { useMemo } from 'react';
import { Redirect, Route, RouteProps } from 'react-router-dom';
import { loginRoute, migrateFromEngage, noSicRoute, supportRoute } from '.';
import AuthRequestStatus from '../enums/AuthRequestStatus';
import { UserRoles, userRoles } from '../sic-lib/types/UserRoles';
import { isUserInRole } from '../sic-lib/utils/userHelpers';
import { AuthRoutesState } from '../types/AuthRoutesState';

interface Props extends RouteProps {
    requiredRoles: UserRoles[];
    authRoutesState: AuthRoutesState;
}

/**
 * Route wrapper for components which require user to have appropriate authentication, authorization.
 * Redirects user to root route if given valid authentication but invalid authorization.
 * Redirects user to login screen if no valid credentials supplied.
 * @param param0
 */
export default function ProtectedRoute({
    authRoutesState,
    children,
    requiredRoles,
    ...rest
}: Props) {
    const hasRequiredRole = useMemo(() => {
        return isUserInRole(authRoutesState.userRoles, requiredRoles);
    }, [authRoutesState.userRoles, requiredRoles]);

    const redirectSupport = useMemo(() => {
        return (
            isUserInRole(authRoutesState.userRoles, userRoles.support) && !authRoutesState.clientId
        );
    }, [authRoutesState.userRoles, authRoutesState.clientId]);

    return (
        <Route
            {...rest}
            render={({ location }) => {
                if (authRoutesState.requestStatus === AuthRequestStatus.configSuccess) {
                    //logged in

                    if (redirectSupport && rest.path !== supportRoute) {
                        return <Redirect to={{ pathname: supportRoute }} />;
                    }

                    //sic not active for client
                    if (!authRoutesState.clientHasSic) {
                        return (
                            <Redirect to={{ pathname: noSicRoute, state: { from: location } }} />
                        );
                    }

                    //activate sic screen
                    if (
                        authRoutesState.dialerIsEnabled &&
                        authRoutesState.dialerIsSetup &&
                        !authRoutesState.dialerIsSic
                    ) {
                        return (
                            <Redirect
                                to={{ pathname: migrateFromEngage, state: { from: location } }}
                            />
                        );
                    }

                    //check if user has required role
                    if (hasRequiredRole) {
                        return <>{children}</>;
                    } else {
                        //redirect home if user doesn't have required role
                        return <Redirect to={{ pathname: '/', state: { from: location } }} />;
                    }
                } else {
                    //just show them the login screen
                    return <Redirect to={{ pathname: loginRoute, state: { from: location } }} />;
                }
            }}
        />
    );
}
